'use client';

import React from 'react';

import { ThumbsDownIcon, ThumbsUpIcon } from 'lucide-react';
import { CreateCouponVotePayload, VoteType } from '@repo/common/types';
import { useLocalToken } from '@/hooks/use-local-token';
import { useCouponVotes } from '@/hooks/use-coupon-votes';
import { cn } from '@/lib/utils/css';
import { Button } from '@/components/ui/button';
import { usePlausible } from 'next-plausible';

type Props = {
  couponId: string;
  onVoteAction: (payload: CreateCouponVotePayload) => Promise<void>;
  urlSlug: string;
  healthy: boolean;
};

export default function CouponVote({
  couponId,
  onVoteAction,
  urlSlug,
  healthy,
}: Props) {
  const localToken = useLocalToken();
  const { setVote, getVote } = useCouponVotes();
  const plausible = usePlausible();

  const currentVote = getVote(couponId);

  const isUpVoted = currentVote === 'up';
  const isDownVoted = currentVote === 'down';

  const handleVote = async (voteType: VoteType) => {
    if (!localToken) return;

    try {
      plausible('coupon-voted', {
        props: { voteType, couponId, urlSlug, healthy },
      });

      await onVoteAction({ couponId, voteType, localToken });
      setVote(couponId, voteType);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="mt-14 flex flex-col items-center justify-center gap-3">
      <span className="text-sm">Did this code work?</span>
      <div className="flex gap-5">
        <Button
          variant="outline"
          onClick={() => handleVote('up')}
          className="flex items-center gap-2 rounded-full px-5"
        >
          <ThumbsUpIcon
            size={20}
            className={cn(
              { 'cursor-pointer': !isUpVoted || !isDownVoted },
              'text-green-500',
            )}
            fill={isUpVoted ? 'currentColor' : 'none'}
            onClick={() => handleVote('up')}
          />
          Yes
        </Button>
        <Button
          variant="outline"
          onClick={() => handleVote('down')}
          className="flex items-center gap-2 rounded-full px-5"
        >
          <ThumbsDownIcon
            size={20}
            className={cn(
              { 'cursor-pointer': !isUpVoted || !isDownVoted },
              'text-red-500',
            )}
            fill={isDownVoted ? 'currentColor' : 'none'}
            onClick={() => handleVote('down')}
          />
          No
        </Button>
      </div>
    </div>
  );
}
